import axios from 'axios';
import type { ApiResponse, QuestionAnswer } from './types';

// Ensure API key is always available
const API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
if (!API_KEY) {
  console.error('OpenRouter API key is missing! Please check your .env file.');
}

const API_URL = "https://openrouter.ai/api/v1/chat/completions";

export async function validateApi(): Promise<boolean> {
  if (!API_KEY) return false;
  
  try {
    const response = await axios.post<ApiResponse>(
      API_URL,
      {
        model: "qwen/qwen2.5-vl-72b-instruct:free",
        messages: [{ role: "user", content: "Hello" }]
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${API_KEY}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "School Decision Helper"
        }
      }
    );
    
    return response.status === 200 && !!response.data.choices;
  } catch (error) {
    console.error('API validation failed:', error);
    return false;
  }
}

export async function getInitialQuestion(): Promise<string> {
  if (!API_KEY) return "Error: API key not configured";
  
  try {
    const response = await axios.post<ApiResponse>(
      API_URL,
      {
        model: "qwen/qwen2.5-vl-72b-instruct:free",
        messages: [{ 
          role: "user", 
          content: "Generate a random, funny yes/no/maybe question about skipping school. Make it unique and relatable. Examples:\n" +
            "- Did your dog actually eat your homework this time?\n" +
            "- Is your bed giving you that 'don't leave me' look?\n" +
            "- Did you spend all night watching cat videos instead of studying?\n" +
            "- Is your crush's bestie spreading rumors about you?\n" +
            "Keep it short, casual, and unexpected! Don't use any of the example questions."
        }]
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${API_KEY}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "School Decision Helper"
        }
      }
    );

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Failed to get initial question:', error);
    return "Did your alarm clock give up on you today?";
  }
}

export async function getNextQuestion(history: QuestionAnswer[]): Promise<string> {
  if (!API_KEY) throw new Error("API key not configured");
  
  try {
    const lastAnswer = history[history.length - 1].answer;
    const historyText = history
      .map(qa => `Question: ${qa.question}\nAnswer: ${qa.answer}`)
      .join('\n');

    const response = await axios.post<ApiResponse>(
      API_URL,
      {
        model: "qwen/qwen2.5-vl-72b-instruct:free",
        messages: [{ 
          role: "user", 
          content: `Previous Q&A:\n${historyText}\n\nLast answer was: ${lastAnswer}\n\n` +
            `Generate a unique follow-up yes/no/maybe question about skipping school. Based on their last answer (${lastAnswer}), make it either:\n` +
            `- Humorously judgmental if they said "No" (like "Is the thought of perfect attendance keeping you up at night?" or "Are you secretly a teacher's pet?")\n` +
            `- Enabler-style if they said "Yes" (like "Did you already plan which games you'll play all day?" or "Is Netflix calling your name louder than your teachers?")\n` +
            `- Extra chaotic if they said "Maybe" (like "Are you planning to show up just for lunch?" or "Did you flip a coin to decide?")\n\n` +
            `If this is the 3rd or 4th question, instead respond with "FINAL_DECISION:" followed by a funny but brutally honest decision about whether they should go to school, based on their pattern of answers.\n\n` +
            `Keep responses short, witty, and original! Don't use any of the example questions.`
        }]
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${API_KEY}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "School Decision Helper"
        }
      }
    );

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Failed to get next question:', error);
    throw new Error('Failed to get next question. The AI is probably taking a sick day too.');
  }
}